import React, { Component } from 'react';
import { createRefetchContainer, graphql, RelayRefetchProp } from 'react-relay';
import PropTypes from 'prop-types';

import { NavSearch } from '@onehope/app-shell';

import ShopWithCeoAddMutation from '../../../mutationsModern/User/ShopWithCeoAddMutation';

import { SearchBarNav_user } from './__generated__/SearchBarNav_user.graphql';
import { oneCommerceImageHost } from '../../../../server/utils/urlConstants';
import { trackEvent } from '../../../utils/tracking';
import {
  urlProductCatalogPage,
  urlTastingFlightLandingPage,
} from '../../../../server/utils/routeConstants';

// not using urlProfile from '/server/utils/urlConstants'
// since it has a trailing slash
const urlProfile = `${oneCommerceImageHost}/profile`;

interface SearchBarNavProps {
  toggleSearch: () => void;
  handleCartHotSwap: () => void;
  handleShopClose: () => void;
  searchCEOS: boolean;
  searchProducts: boolean;
  searchEvents: boolean;
  user: SearchBarNav_user;
  relay: RelayRefetchProp;
  resetSearch: () => void;
}

interface SearchBarNavState {
  isMenuOpen: boolean;
  inputValue: string;
  selectedCeoId: string;
  isLoadingData: boolean;
  ceoId: string | undefined;
  cachedInput: string;
}

class SearchBarNav extends Component<SearchBarNavProps, SearchBarNavState> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  constructor(props: SearchBarNavProps) {
    super(props);
    this.state = {
      inputValue: '',
      cachedInput: '',
      isMenuOpen: true,
      ceoId: undefined,
      selectedCeoId: '',
      isLoadingData: false,
    };
  }

  componentWillUnmount() {
    this.props.resetSearch();
  }

  handleLoadingData = (isLoadingData: boolean) => {
    this.setState({ isLoadingData });
  };

  fetchInput = (inputValue: string) => {
    const sanitizedText = inputValue.replace(/\s/g, '');
    if (sanitizedText !== '') {
      if (this.state.isLoadingData) {
        return this.setState({ cachedInput: inputValue });
      }
      const { searchCEOS, searchProducts, searchEvents } = this.props;
      this.handleLoadingData(true);
      const reFetchVariables = () => {
        return {
          ceoId: 'none',
          inputValue,
          searchEvents,
          searchProducts,
          searchCEOS,
        };
      };

      this.props.relay.refetch(
        reFetchVariables,
        undefined,
        () => {
          this.handleLoadingData(false);
          const { cachedInput } = this.state;
          if (cachedInput) {
            this.setState({ cachedInput: '' }, () =>
              this.fetchInput(cachedInput),
            );
          }
        },
        {
          force: true,
        },
      );
    }
  };

  handleInputChange = (e: any) => {
    const inputValue = e.target.value;
    const { isMenuOpen } = this.state;
    if (!isMenuOpen) {
      this.openMenu();
    }
    this.setState({ inputValue }, () => this.fetchInput(inputValue));
  };

  closeMenu = () => {
    this.setState({ isMenuOpen: false });
  };

  openMenu = () => {
    this.setState({ isMenuOpen: true });
  };

  clearInput = () => {
    this.setState({ inputValue: '' });
  };

  handleSelect = (selectedItem: any) => {
    const { toggleSearch, user, handleShopClose } = this.props;
    const { router } = this.context;
    const { routePath, isProduct, isEvent, isCEO, ceoId } = selectedItem.node;
    const { permissions } = user;
    const isLoggedInAsCeo =
      permissions &&
      permissions.oneCommerce &&
      permissions.oneCommerce.ceo &&
      permissions.oneCommerce.ceo.access;
    trackEvent({
      user,
      event: 'Products Searched',
      properties: {
        query: this.state.inputValue,
        itemClicked: selectedItem.node.primaryLabel,
      },
    });
    if ((!isCEO && isProduct) || isEvent) {
      this.closeMenu();
      toggleSearch();
      this.clearInput();
      if (isEvent) handleShopClose();
      if (routePath.match('/tasting-flight')) {
        window.location.href = urlTastingFlightLandingPage;
        return;
      }
      if (isProduct) {
        window.location.href = `${urlProductCatalogPage}${routePath}`;
        return;
      }
      return router.push(routePath);
    }
    if (isCEO && !isLoggedInAsCeo) {
      this.shopWithCeo(ceoId, () => {});
    }
    if (isCEO) {
      return router.push(routePath);
    }
  };

  onRegistrationComplete = (callback: () => void = () => {}) => {
    const { handleCartHotSwap, toggleSearch } = this.props;
    handleCartHotSwap();
    this.closeMenu();
    this.clearInput();
    toggleSearch();
    if (typeof callback === 'function') callback();
  };

  onRegistrationError = (errors: Object) => {
    console.log('error in register mutation', errors);
  };

  shopWithCeo = (ceoId: string, callback: () => void = () => {}) => {
    const {
      relay: { environment },
    } = this.props;
    const onCompleted = () => this.onRegistrationComplete(callback);
    const onError = this.onRegistrationError;
    return ShopWithCeoAddMutation.commit({
      ceoId,
      environment,
      onCompleted,
      onError,
    });
  };

  render() {
    const { user } = this.props;
    const { inputValue, isMenuOpen } = this.state;
    if (!user) return null;
    const { autoCompleteData } = user;
    const sanitizedText = inputValue.replace(/\s/g, '');
    const items =
      sanitizedText === ''
        ? []
        : autoCompleteData
        ? autoCompleteData.edges
        : [];
    const { searchCEOS, searchProducts } = this.props;
    return (
      <NavSearch
        searchCEOS={searchCEOS}
        searchProducts={searchProducts}
        inputValue={inputValue}
        isSearchOpen={isMenuOpen}
        handleSearchClose={this.closeMenu}
        handleChange={this.handleInputChange}
        handleSelect={this.handleSelect}
        suggestions={items}
        urlProfile={urlProfile}
      />
    );
  }
}

export default createRefetchContainer(
  SearchBarNav,
  {
    user: graphql`
      fragment SearchBarNav_user on User
        @argumentDefinitions(
          ceoId: { type: "String", defaultValue: "none" }
          inputValue: { type: "String", defaultValue: null }
          searchCEOS: { type: "Boolean", defaultValue: false }
          searchEvents: { type: "Boolean", defaultValue: false }
          searchProducts: { type: "Boolean", defaultValue: false }
        ) {
        ...AuthDialog_user
        ...ShopWithCeoDialog_user
        userId
        shoppingWithCeoUser {
          id
          publicId
          userId
          firstName
          lastName
          profile {
            id
            image
          }
        }
        permissions {
          id
          oneCommerce {
            id
            ceo {
              access
            }
          }
        }
        conciergeUser {
          userId
        }
        conciergeCustomerAccountId
        cart {
          id
          eventId
          event {
            id
            trinityPartyType
          }
        }
        autoCompleteData(
          first: 10
          ceoId: $ceoId
          inputValue: $inputValue
          searchCEOS: $searchCEOS
          searchEvents: $searchEvents
          searchProducts: $searchProducts
        ) {
          edges {
            node {
              id
              isCEO
              ceoId
              image
              isEvent
              isProduct
              routePath
              primaryLabel
              secondaryLabel
              productCategory
            }
          }
        }
      }
    `,
  },
  graphql`
    query SearchBarNavRefetchQuery(
      $ceoId: String
      $inputValue: String
      $searchCEOS: Boolean
      $searchEvents: Boolean
      $searchProducts: Boolean
    ) {
      viewer {
        id
        id
        user {
          id
          ...SearchBarNav_user
            @arguments(
              ceoId: $ceoId
              inputValue: $inputValue
              searchCEOS: $searchCEOS
              searchEvents: $searchEvents
              searchProducts: $searchProducts
            )
        }
      }
    }
  `,
);
